// Index = encoded value, value at location = decoded character

import { type StaticAirport } from '../static/static-airports';
import * as turf from '@turf/turf';
import dayjs, { Dayjs } from 'dayjs';
import { UDViewMapStyle } from '../udView/udview';
import { WorkerGlobalScopeInterface } from '../worker/flight-worker';
import { RouteFlownFuelUnit } from '../messages/message-json';
import { FuelUnit } from '@fe/core';

const pcCoded2Char = [
    32, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87,
    88, 89, 90, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 34
] as const;

export function toAsciiBytes(input: string, size: number, filler: string = '\0'): Uint8Array {
    const array = new Uint8Array(size);

    for (let i = 0; i < size; i++) {
        array[i] = filler.charCodeAt(0);
    }

    for (let i = 0; i < Math.min(size, input.length); i++) {
        array[i] = input.charCodeAt(i);
    }

    return array;
}

export function writeAsciiBytes(view: DataView, offset: number, input: string, size: number, filler: string) {
    for (let i = 0; i < size; i++) {
        if (i < input.length) {
            view.setUint8(i + offset, input.charCodeAt(i));
        } else {
            view.setUint8(i + offset, filler.charCodeAt(0));
        }
    }

    return view;
}

export function writeJulianDateOffset(view: DataView, offset: number, date: Dayjs | null, overrideTodayAsZero: boolean = false): DataView {
    //     DWORD dwEDTJulianDay;  // Jan 1, 0001 AD is day number 1 (0=current day) [⚠️NOT ALWAYS 0=CURRENT DAY! EVEN IF COMMENT IN THE C++ CODE SAYS SO! (im going to lose it)]
    const daysSince2000 = 730120; // Jan 1, 2000 is day number 730120, needed since the epoch that everyone uses is Jan 1, 1970, so Dayjs doesnt support such small dates

    if (date === null || !date.isValid() || date.utc().isSame(dayjs.utc(), 'day')) {
        view.setUint32(offset, overrideTodayAsZero && date ? date.diff(dayjs('2000-01-01'), 'day') + daysSince2000 : 0, true);
        return view;
    }

    const julianOffset = date.diff(dayjs('2000-01-01'), 'day') + daysSince2000;

    view.setUint32(offset, julianOffset, true);
    return view;
}

export function writeFEWPTime(view: DataView, offset: number, date: Dayjs | null): DataView {
    // explained in the C++ code:
    // "FEWP time is time in seconds since...?"

    if (date === null || !date.isValid()) {
        view.setUint32(offset, 0, true);
        return view;
    }

    view.setUint32(offset, date.diff(dayjs('2000-01-01'), 'second') + 3652 * 86400, true);
    return view;
}

export function julianOffsetToDate(
    hour: number,
    minute: number,
    julianDayOffset: number,
    overrideTodayAsZero: boolean = false
): Date | null {
    // Jan 1, 0001 AD is day number 1 (0=current day)
    // Jan 1, 2000 is day number 730120
    const daysSince2000 = 730120;
    if (hour === 24) {
        return null;
    }

    // If julianDayOffset is 0 and overrideTodayAsZero is false, it means "current day"
    if (julianDayOffset === 0 && !overrideTodayAsZero) {
        return dayjs.utc().startOf('day').hour(hour).minute(minute).toDate();
    }

    // Otherwise, calculate from Jan 1, 2000
    const date = dayjs('2000-01-01')
        .utc()
        .startOf('day')
        .add(julianDayOffset - daysSince2000, 'day');
    return date.hour(hour).minute(minute).toDate();
}

export function toAsciiBytesZeroTerminated(input: string, size: number, filler: string = '\0'): Uint8Array {
    const array = toAsciiBytes(input, size, filler);
    array[size - 1] = 0;
    return array;
}

export function alphNum2Char(cAlphNum: number): string {
    if (cAlphNum > 63) {
        return '';
    }

    return String.fromCharCode(pcCoded2Char[cAlphNum]);
}

export function guidToString(input: number[]) {
    return input.map((x) => (0x100 + x).toString(16).slice(1)).join('');
}

const toNormalizeRegex = new RegExp(/^[A-Z]{3}\d{1,3}.{0,1}$/);

export function normalizeAcid(acid: string): string {
    if (!toNormalizeRegex.test(acid)) {
        return acid;
    }

    const last = acid.charAt(acid.length - 1);
    const len = last >= 'A' && last <= 'Z' ? 5 : 4;
    return acid.substring(0, 3) + acid.substring(3).padStart(len, '0');
}

export function airportsEqual(id: string, airport: StaticAirport): boolean {
    return id === airport.icao || id === airport.iata || id === airport.faa;
}

export function isSameDate(date1: number, date2: number): boolean {
    return date1 - (date1 % 86_400) === date2 - (date2 % 86_400);
}

export function getHourNumber(date: number): number {
    return Math.floor((date % 86_400) / 3600);
}

export function greatCircle(a: GeoJSON.Position, b: GeoJSON.Position): GeoJSON.LineString | GeoJSON.MultiLineString {
    const distance = turf.distance(a, b, { units: 'nauticalmiles' });
    if (distance > 50) {
        const feature = turf.greatCircle(a, b, { npoints: Math.floor(distance / 20) });
        return feature.geometry;
    }

    return {
        type: 'LineString',
        coordinates: fix180(a, b)
    };
}

export function fix180(start: GeoJSON.Position, end: GeoJSON.Position): [GeoJSON.Position, GeoJSON.Position] {
    const startLng = start[0];
    let endLng = end[0];

    if (endLng - startLng >= 180) {
        endLng -= 360;
    } else if (endLng - startLng <= -180) {
        endLng += 360;
    }

    return [start, [endLng, end[1]]];
}

export function isLight(style: UDViewMapStyle) {
    return style === UDViewMapStyle.light || style === UDViewMapStyle.streets || style === UDViewMapStyle.terrainStreets;
}

/** If true extended File System Access API is available. NOTE: this will always be false in web worker scope */
export const FileSystemExtendedAvailable = globalThis.window && 'showOpenFilePicker' in globalThis.window;

export function isWorker(self: any): self is WorkerGlobalScopeInterface {
    return typeof WorkerGlobalScope !== 'undefined' && typeof self !== 'undefined' && self instanceof WorkerGlobalScope;
}

export function routeFlownUnitToFuelUnit(unit: RouteFlownFuelUnit | null): FuelUnit | null {
    switch (unit) {
        case null:
            return null;
        case RouteFlownFuelUnit.volumeGallons:
            return FuelUnit.VolumeGallons;
        case RouteFlownFuelUnit.weightPounds:
            return FuelUnit.WeighPounds;
    }
}